/**
 * Resolves the MDI slugs stored on icon annotations to renderable SVG path data.
 *
 * `@mdi/js` is a single module of path strings for the whole Material Design Icons set,
 * loaded lazily so it only costs anything once the editor is actually opened. It is
 * deliberately not the CDN fetch the original floorplan tool used: this needs no network at
 * runtime, and no per-icon request.
 *
 * The set here is MDI 7.x, which is also what the mobile app renders from
 * (`material_design_icons_flutter` 7.0.7296). Keeping them on the same major matters —
 * an icon that only exists in a later MDI renders as nothing on the app, silently.
 */

/** Every icon path, keyed by its `mdi`-prefixed camelCase name, once the module has loaded. */
type IconPathModule = Record<string, string>;

let iconPathModule: IconPathModule | undefined;
let loadPromise: Promise<IconPathModule> | undefined;

/** `fire-extinguisher` → `mdiFireExtinguisher`. Names arrive kebab-cased and without the `mdi-` prefix. */
export const toMdiExportName = (iconName: string): string => {
  const normalized = iconName.trim().replace(/^mdi-/, '');
  if (!normalized) return '';

  const pascalCase = normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

  return `mdi${pascalCase}`;
};

export const loadMdiIconPaths = async (): Promise<IconPathModule> => {
  if (iconPathModule) return iconPathModule;

  // Shared, so a plan with fifty icon annotations still loads the module exactly once.
  loadPromise ??= import('@mdi/js').then((module) => {
    iconPathModule = module as unknown as IconPathModule;
    return iconPathModule;
  });

  return loadPromise;
};

/**
 * The path data for an icon, or undefined when the module has not loaded yet or the name is
 * not a real MDI icon. Callers render a visible placeholder for the latter rather than
 * nothing: on the app an unknown name draws silently nothing, so the editor is the only
 * place the mistake can be seen.
 */
export const getMdiIconPath = (iconName: string | null): string | undefined => {
  if (!iconName || !iconPathModule) return undefined;

  const path = iconPathModule[toMdiExportName(iconName)];
  return typeof path === 'string' ? path : undefined;
};

export const isKnownMdiIcon = (iconName: string | null): boolean => getMdiIconPath(iconName) !== undefined;

/** `mdiFireExtinguisher` → `fire-extinguisher`, the form the layers store. */
const toIconSlug = (exportName: string): string => exportName
  .replace(/^mdi/, '')
  .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
  .toLowerCase();

/**
 * Icon slugs matching a search term.
 *
 * Searches the loaded module's own export names, so the whole set is reachable rather than
 * just a curated shortlist — an admin looking for something specific should not be limited
 * to what someone thought to list.
 */
export const searchMdiIcons = (query: string, limit: number): string[] => {
  if (!iconPathModule) {
    return [];
  }

  const term = query.trim().toLowerCase().replace(/^mdi-/, '');
  if (!term) {
    return [];
  }

  const matches: string[] = [];

  Object.keys(iconPathModule).some((exportName) => {
    const slug = toIconSlug(exportName);
    if (slug.includes(term)) {
      matches.push(slug);
    }

    return matches.length >= limit;
  });

  return matches;
};

/** MDI's own glyphs are drawn on a 24×24 canvas; every size in the layers is relative to it. */
export const MDI_VIEWBOX_SIZE = 24;
